import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { toast } from "react-toastify";

// Scale Context erstellen
const ScaleContext = createContext();

// Custom Hook zum Verwenden des Scale Context
export const useScale = () => useContext(ScaleContext);

// Scale Provider Component
export const ScaleProvider = ({ children }) => {
  // Zustandsvariablen für die Waage
  const [showScaleModal, setShowScaleModal] = useState(false);
  const [port, setPort] = useState(null);
  const [reader, setReader] = useState(null);
  const [serialConnected, setSerialConnected] = useState(false);
  const [currentWeight, setCurrentWeight] = useState("0.00");
  const [, setReadingWeight] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [debugLogs, setDebugLogs] = useState([]);
  const [waitingForConfirmation, setWaitingForConfirmation] = useState(false);
  const [onConfirmationCallback, setOnConfirmationCallback] = useState(null);
  const [lastWeightEventTimestamp, setLastWeightEventTimestamp] = useState(0);
  const [lastTareTimestamp, setLastTareTimestamp] = useState(0);
  const [connectionSettings, setConnectionSettings] = useState({
    baudRate: 9600,
    dataBits: 8,
    stopBits: 1,
    parity: "odd",
    flowControl: "none",
  });

  // Encoder/Decoder für die serielle Kommunikation
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const encoder = new TextEncoder();
  const decoder = new TextDecoder();

  // Modal-Steuerung
  const openScaleModal = () => setShowScaleModal(true);
  const closeScaleModal = () => setShowScaleModal(false);

  // Debug-Log-Funktion
  const addDebugLog = useCallback((message, type = "info") => {
    const timestamp = new Date().toISOString().substr(11, 8);
    const newLog = { timestamp, message, type };
    setDebugLogs((prevLogs) => [newLog, ...prevLogs].slice(0, 50));
  }, []);

  // Gewicht zurücksetzen (für neue Bestellungen)
  const resetWeight = useCallback(() => {
    setCurrentWeight("0.00");
    addDebugLog("🔄 Gewicht auf 0.00 zurückgesetzt", "info");
  }, [addDebugLog]);

  // Tare-Freigabe signalisieren (App-Button oder erkanntes Waagen-Signal)
  const markTareCompleted = useCallback(
    (source = "unknown") => {
      const ts = Date.now();
      setLastTareTimestamp(ts);
      addDebugLog(`⚖️ Tara bestätigt (${source}) @ ${ts}`, "success");
    },
    [addDebugLog],
  );

  // Web Serial API Unterstützung prüfen
  const isWebSerialSupported = useCallback(() => {
    return "serial" in navigator;
  }, []);

  // Aktualisierung der Verbindungseinstellungen
  const updateConnectionSettings = useCallback((newSettings) => {
    setConnectionSettings((prev) => ({ ...prev, ...newSettings }));
  }, []);

  // Port auswählen
  const selectPort = useCallback(async () => {
    if (!isWebSerialSupported()) {
      addDebugLog(
        "Web Serial API wird von diesem Browser nicht unterstützt",
        "error",
      );
      toast.error("Web Serial API wird von diesem Browser nicht unterstützt");
      return;
    }

    try {
      addDebugLog("Port-Auswahl-Dialog geöffnet");
      const selectedPort = await navigator.serial.requestPort();
      setPort(selectedPort);
      addDebugLog("Port erfolgreich ausgewählt", "success");

      // Information über den Port anzeigen
      const info = selectedPort.getInfo ? selectedPort.getInfo() : {};
      if (info.usbVendorId) {
        addDebugLog(
          `USB Vendor ID: 0x${info.usbVendorId.toString(16).padStart(4, "0")}`,
        );
      }
      if (info.usbProductId) {
        addDebugLog(
          `USB Product ID: 0x${info.usbProductId.toString(16).padStart(4, "0")}`,
        );
      }

      toast.success("Port erfolgreich ausgewählt");
      return selectedPort;
    } catch (error) {
      console.error("Fehler bei der Port-Auswahl:", error);
      addDebugLog(`Fehler bei der Port-Auswahl: ${error.message}`, "error");
      toast.error("Port-Auswahl abgebrochen oder fehlgeschlagen");
      return null;
    }
  }, [addDebugLog, isWebSerialSupported]);

  // Verbindung zur Waage herstellen
  const connectScale = useCallback(async () => {
    let selectedPort = port;

    if (!selectedPort) {
      selectedPort = await selectPort();
      if (!selectedPort) return;
    }

    try {
      addDebugLog("Verbinde mit der Waage...");

      let flowControlOption = {};
      switch (connectionSettings.flowControl) {
        case "hardware":
          flowControlOption = { rtscts: true };
          break;
        case "software":
          flowControlOption = { xon: true, xoff: true };
          break;
        default:
          flowControlOption = {};
      }

      // Konfigurieren und öffnen des seriellen Ports für Sartorius Waage
      const options = {
        baudRate: connectionSettings.baudRate,
        dataBits: connectionSettings.dataBits,
        stopBits: connectionSettings.stopBits,
        parity: connectionSettings.parity,
        ...flowControlOption,
        bufferSize: 1024,
      };

      addDebugLog(`Konfiguriere Port mit: ${JSON.stringify(options)}`);
      await selectedPort.open(options);
      setPort(selectedPort);

      addDebugLog("Port erfolgreich geöffnet", "success");
      setSerialConnected(true);

      // Starten des kontinuierlichen Lesevorgangs
      startReading(selectedPort);

      toast.success("Verbindung zur Waage hergestellt");
      return true;
    } catch (error) {
      console.error("Fehler beim Verbinden mit der Waage:", error);
      addDebugLog(
        `Fehler beim Verbinden mit der Waage: ${error.message}`,
        "error",
      );
      toast.error(`Fehler beim Verbinden mit der Waage: ${error.message}`);
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addDebugLog, connectionSettings, port, selectPort]);

  // Trennen der Waage
  const disconnectScale = useCallback(async () => {
    if (!port) {
      addDebugLog("Keine aktive Verbindung vorhanden", "info");
      return;
    }

    try {
      addDebugLog("Trenne Verbindung zur Waage...");

      // Reader schließen, wenn vorhanden
      if (reader) {
        try {
          await reader.cancel();
          addDebugLog("Leseprozess gestoppt", "info");
        } catch (err) {
          addDebugLog(
            `Fehler beim Stoppen des Lesers: ${err.message}`,
            "warning",
          );
        }
        setReader(null);
      }

      // Port schließen
      try {
        await port.close();
        addDebugLog("Port erfolgreich geschlossen", "success");
      } catch (err) {
        addDebugLog(
          `Fehler beim Schließen des Ports: ${err.message}`,
          "warning",
        );
      }

      setSerialConnected(false);
      setCurrentWeight("0.00");

      toast.info("Verbindung zur Waage getrennt");
    } catch (error) {
      console.error("Fehler beim Trennen der Waage:", error);
      addDebugLog(`Fehler beim Trennen der Waage: ${error.message}`, "error");
      toast.error("Fehler beim Trennen der Waage");
    }
  }, [addDebugLog, port, reader]);

  // Kontinuierliches Lesen der Waagendaten
  const startReading = useCallback(
    async (selectedPort) => {
      if (!selectedPort) selectedPort = port;
      if (!selectedPort) {
        addDebugLog("Kein Port verfügbar zum Lesen", "error");
        return;
      }

      try {
        addDebugLog("Starte kontinuierliches Lesen...");
        setReadingWeight(true);

        // Lesen
        while (selectedPort.readable) {
          const newReader = selectedPort.readable.getReader();
          setReader(newReader);
          addDebugLog("Reader erfolgreich erstellt", "success");

          try {
            let buffer = "";

            // Initialer Tare-Befehl
            await sendInitialCommand(selectedPort);

            // Kontinuierliches Lesen
            while (true) {
              const { value, done } = await newReader.read();
              if (done) {
                addDebugLog("Lesevorgang abgebrochen", "info");
                break;
              }

              // Neue Daten zum Buffer hinzufügen
              const text = decoder.decode(value);
              buffer += text;

              // Nach vollständigen Datensätzen suchen (mit Zeilenumbrüchen)
              if (buffer.includes("\r") || buffer.includes("\n")) {
                // Buffer verarbeiten
                processWeightData(buffer);
                buffer = "";
              }
            }
          } catch (error) {
            console.error("Fehler beim Lesen der Waagendaten:", error);
            addDebugLog(
              `Fehler beim Lesen der Waagendaten: ${error.message}`,
              "error",
            );
          } finally {
            newReader.releaseLock();
            setReader(null);
          }
        }
      } catch (error) {
        console.error("Fehler beim Starten des Lesevorgangs:", error);
        addDebugLog(
          `Fehler beim Starten des Lesevorgangs: ${error.message}`,
          "error",
        );
      } finally {
        setReadingWeight(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addDebugLog, decoder, port],
  );

  // Verarbeitung der Waagendaten
  const processWeightData = useCallback(
    (data) => {
      // IMMER loggen wenn processWeightData aufgerufen wird
      addDebugLog(
        `📞 PROCESS WEIGHT DATA AUFGERUFEN mit: "${data.replace(/\r/g, "\\r").replace(/\n/g, "\\n")}"`,
        "info",
      );
      addDebugLog(
        `📊 Aktueller Zustand - waitingForConfirmation: ${waitingForConfirmation}, onConfirmationCallback: ${!!onConfirmationCallback}`,
        "info",
      );

      try {
        if (debugMode) {
          addDebugLog(
            `Rohdaten empfangen: ${data.replace(/\r/g, "\\r").replace(/\n/g, "\\n")}`,
            "debug",
          );
        }

        let weightDetected = false;
        let tareSignalDetected = false;

        // Physische Tare-Taste / Tara-Antwort der Waage (Sartorius u. a.)
        // Rohdaten können z. B. Tare-Quittungen oder ESC-kT-Echo enthalten.
        const normalizedData = data.replace(/\x1b/g, "ESC");
        if (/\bkT\b/i.test(normalizedData) || /\bTARE\b/i.test(normalizedData)) {
          tareSignalDetected = true;
          addDebugLog("⚖️ Tare-Signal in Waagendaten erkannt", "success");
        }

        // Sartorius Format: Gewicht steht nach "N"
        if (data.includes("N")) {
          // Sartorius Standard Format mit "N" für Nettogewicht
          const match = data.match(/N\s+([+-]?\d+\.\d+)\s+g/);
          if (match && match[1]) {
            const weight = match[1];
            setCurrentWeight(weight);
            addDebugLog(
              `✅ Gewicht erkannt (N-Format): ${weight} g`,
              "success",
            );
            weightDetected = true;
          }
        }

        // Sartorius SBI Format
        if (!weightDetected) {
          const sbiMatch = data.match(/\s+([+-]?\d+\.\d+)\s+g/);
          if (sbiMatch && sbiMatch[1]) {
            const weight = sbiMatch[1];
            setCurrentWeight(weight);
            addDebugLog(
              `✅ Gewicht erkannt (SBI-Format): ${weight} g`,
              "success",
            );
            weightDetected = true;
          }
        }

        // Gewicht ohne "g" Einheit
        if (!weightDetected) {
          const numericMatch = data.match(/\s+([+-]?\d+\.\d+)/);
          if (numericMatch && numericMatch[1]) {
            const weight = numericMatch[1];
            setCurrentWeight(weight);
            addDebugLog(`✅ Gewicht erkannt (Numerisch): ${weight}`, "success");
            weightDetected = true;
          }
        }

        // Bei JEDEM Waagen-Signal Timestamp aktualisieren (auch ohne Gewichtsänderung)
        if (weightDetected || data.trim().length > 0) {
          const timestamp = Date.now();
          setLastWeightEventTimestamp(timestamp);
          addDebugLog(
            `⏰ WAAGEN-EVENT TIMESTAMP AKTUALISIERT: ${timestamp}`,
            "info",
          );
        }

        if (tareSignalDetected) {
          markTareCompleted("scale-signal");
        }

        // Debug-Log für Bestätigungslogik
        addDebugLog(
          `🔍 BESTÄTIGUNGS-CHECK: weightDetected=${weightDetected}, waitingForConfirmation=${waitingForConfirmation}, onConfirmationCallback=${!!onConfirmationCallback}`,
          "info",
        );

        // Wenn Gewicht erkannt wurde UND wir auf Bestätigung warten
        if (
          weightDetected &&
          waitingForConfirmation &&
          onConfirmationCallback
        ) {
          addDebugLog(
            `🔥 PHYSISCHE WAAGE: Gewichtsdaten empfangen - LÖSE BESTÄTIGUNG AUS! 🔥`,
            "success",
          );

          // Callback ausführen (das ist unser queryWeight wrapper)
          try {
            addDebugLog(`📞 FÜHRE BESTÄTIGUNGS-CALLBACK AUS...`, "info");
            onConfirmationCallback();
            addDebugLog(
              `✅ Bestätigungs-Callback erfolgreich ausgeführt`,
              "success",
            );
          } catch (error) {
            addDebugLog(
              `❌ Fehler beim Bestätigungs-Callback: ${error.message}`,
              "error",
            );
          }
        } else {
          if (!weightDetected) {
            addDebugLog(`⚠️ KEIN GEWICHT ERKANNT`, "warning");
          }
          if (!waitingForConfirmation) {
            addDebugLog(`⚠️ NICHT AUF BESTÄTIGUNG WARTEND`, "warning");
          }
          if (!onConfirmationCallback) {
            addDebugLog(`⚠️ KEIN CALLBACK REGISTRIERT`, "warning");
          }
        }

        // Wenn kein Matching Format gefunden wurde
        if (!weightDetected && debugMode) {
          addDebugLog(`❌ Gewichtsformat nicht erkannt in: ${data}`, "warning");
        }
      } catch (error) {
        console.error("Fehler bei der Verarbeitung der Waagendaten:", error);
        addDebugLog(
          `Fehler bei der Verarbeitung der Waagendaten: ${error.message}`,
          "error",
        );
      }
    },
    [addDebugLog, debugMode, waitingForConfirmation, onConfirmationCallback, markTareCompleted],
  );

  // Befehl an die Waage senden
  const sendCommand = useCallback(
    async (command, port) => {
      if (!port && !window.port) {
        addDebugLog("Kein Port für Befehl verfügbar", "error");
        return false;
      }

      const targetPort = port || window.port;

      try {
        addDebugLog(
          `Sende Befehl: ${command.replace(/\r/g, "\\r").replace(/\n/g, "\\n")}`,
          "info",
        );

        if (targetPort.writable) {
          const writer = targetPort.writable.getWriter();

          try {
            await writer.write(encoder.encode(command));
            addDebugLog("Befehl erfolgreich gesendet", "success");
            return true;
          } catch (error) {
            console.error("Fehler beim Senden des Befehls:", error);
            addDebugLog(
              `Fehler beim Senden des Befehls: ${error.message}`,
              "error",
            );
            return false;
          } finally {
            writer.releaseLock();
          }
        } else {
          addDebugLog("Port nicht schreibbar", "error");
          return false;
        }
      } catch (error) {
        console.error("Fehler beim Senden des Befehls:", error);
        addDebugLog(
          `Fehler beim Senden des Befehls: ${error.message}`,
          "error",
        );
        return false;
      }
    },
    [addDebugLog, encoder],
  );

  // Initialen Befehl an die Waage senden
  const sendInitialCommand = useCallback(
    async (targetPort = null) => {
      try {
        // Sartorius SBI Protokoll für kontinuierliches Senden aktivieren
        const result = await sendCommand("K\r\n", targetPort || port);
        if (result) {
          addDebugLog("Kontinuierliches Senden aktiviert", "success");
        }
        return result;
      } catch (error) {
        console.error("Fehler beim Senden des initialen Befehls:", error);
        addDebugLog(
          `Fehler beim Senden des initialen Befehls: ${error.message}`,
          "error",
        );
        return false;
      }
    },
    [addDebugLog, port, sendCommand],
  );

  // Tare-Befehl senden (soft-Tare an der Waage; gilt als Tare-Betätigung)
  const tareScale = useCallback(async () => {
    try {
      const result = await sendCommand("T\r\n", port);
      if (result) {
        addDebugLog("Tara-Befehl gesendet", "success");
        toast.info("Tara-Befehl gesendet");
        markTareCompleted("app-button");
      }
      return result;
    } catch (error) {
      console.error("Fehler beim Senden des Tara-Befehls:", error);
      addDebugLog(
        `Fehler beim Senden des Tara-Befehls: ${error.message}`,
        "error",
      );
      toast.error("Fehler beim Senden des Tara-Befehls");
      return false;
    }
  }, [addDebugLog, port, sendCommand, markTareCompleted]);

  // Zero-Befehl senden
  const zeroScale = useCallback(async () => {
    try {
      const result = await sendCommand("Z\r\n", port);
      if (result) {
        addDebugLog("Zero-Befehl gesendet", "success");
        toast.info("Zero-Befehl gesendet");
      }
      return result;
    } catch (error) {
      console.error("Fehler beim Senden des Zero-Befehls:", error);
      addDebugLog(
        `Fehler beim Senden des Zero-Befehls: ${error.message}`,
        "error",
      );
      toast.error("Fehler beim Senden des Zero-Befehls");
      return false;
    }
  }, [addDebugLog, port, sendCommand]);

  // Print-Befehl senden
  const sendPrintCommand = useCallback(async () => {
    try {
      const result = await sendCommand("P\r\n", port);
      if (result) {
        addDebugLog("Print-Befehl gesendet", "success");
        toast.info("Print-Befehl gesendet");
      }
      return result;
    } catch (error) {
      console.error("Fehler beim Senden des Print-Befehls:", error);
      addDebugLog(
        `Fehler beim Senden des Print-Befehls: ${error.message}`,
        "error",
      );
      toast.error("Fehler beim Senden des Print-Befehls");
      return false;
    }
  }, [addDebugLog, port, sendCommand]);

  // Manuelle Gewichtsabfrage
  const queryWeight = useCallback(async () => {
    try {
      const result = await sendCommand("P\r\n", port);
      if (result) {
        addDebugLog("Gewichtsabfrage gesendet", "success");
      }
      return result;
    } catch (error) {
      console.error("Fehler bei der Gewichtsabfrage:", error);
      addDebugLog(`Fehler bei der Gewichtsabfrage: ${error.message}`, "error");
      return false;
    }
  }, [addDebugLog, port, sendCommand]);

  // Aufräumen beim Unmount
  useEffect(() => {
    return () => {
      // Verbindung trennen, wenn die Komponente unmounted
      if (serialConnected) {
        disconnectScale();
      }
    };
  }, [disconnectScale, serialConnected]);

  // Überwachung von Gewichtsänderungen für physische Waagen-Bestätigung
  useEffect(() => {
    if (
      waitingForConfirmation &&
      onConfirmationCallback &&
      lastWeightEventTimestamp > 0
    ) {
      addDebugLog(
        `🎯 WAAGEN-EVENT ERKANNT - Timestamp: ${lastWeightEventTimestamp}, Gewicht: ${currentWeight}`,
        "info",
      );
      addDebugLog(
        `🔥 WAAGEN-SIGNAL EMPFANGEN (auch ohne Gewichtsänderung) - LÖSE BESTÄTIGUNG AUS! 🔥`,
        "success",
      );

      // Callback ausführen
      try {
        addDebugLog(`📞 FÜHRE TIMESTAMP-CALLBACK AUS...`, "info");
        onConfirmationCallback();
        addDebugLog(`✅ Timestamp-Callback erfolgreich ausgeführt`, "success");
      } catch (error) {
        addDebugLog(
          `❌ Fehler beim Timestamp-Callback: ${error.message}`,
          "error",
        );
      }
    }
  }, [
    lastWeightEventTimestamp,
    waitingForConfirmation,
    onConfirmationCallback,
    addDebugLog,
    currentWeight,
  ]);

  // Context-Wert
  const value = {
    showScaleModal,
    openScaleModal,
    closeScaleModal,
    serialConnected,
    currentWeight,
    connectScale,
    disconnectScale,
    selectPort,
    tareScale,
    zeroScale,
    sendPrintCommand,
    queryWeight,
    debugMode,
    setDebugMode,
    debugLogs,
    connectionSettings,
    updateConnectionSettings,
    isWebSerialSupported,
    waitingForConfirmation,
    setWaitingForConfirmation,
    onConfirmationCallback,
    setOnConfirmationCallback,
    lastWeightEventTimestamp,
    setLastWeightEventTimestamp,
    lastTareTimestamp,
    markTareCompleted,
    resetWeight,
  };

  return (
    <ScaleContext.Provider value={value}>{children}</ScaleContext.Provider>
  );
};

export default ScaleContext;
